import { useEffect, useRef, useState, type ReactNode } from 'react';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Switch from '@mui/material/Switch';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ComputerOutlinedIcon from '@mui/icons-material/ComputerOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';

import SoftBox from 'components/SoftBox';
import SoftButton from 'components/SoftButton';
import SoftTypography from 'components/SoftTypography';

import { DirectUploadError } from 'lib/direct-upload';
import { interpolateTemplate } from 'lib/i18n/dictionary';
import {
  checkGalleryCoverFile,
  coverCandidates,
  createGalleryPreviewTokenRequest,
  FREE_GALLERY_PHOTO_SHAPE,
  FREE_GALLERY_SPACING,
  GALLERY_LAYOUTS,
  GALLERY_PHOTO_SHAPES,
  GALLERY_SPACINGS,
  GALLERY_STYLES,
  galleryPreviewSrc,
  isPaidOptionLocked,
  isPaidSettingLocked,
  PREVIEW_FRAMES,
  previewScale,
  previewTokenRefreshDelay,
  setGalleryCoverRequest,
  showsOriginalsWithoutWatermarkHint,
  updateGalleryRequest,
  uploadGalleryCoverRequest,
  type Gallery,
  type PreviewFrame,
  type UpdateGalleryInput,
} from 'gallery';

import {
  LayoutPictogram,
  ShapePictogram,
  SpacingPictogram,
  StylePictogram,
} from './gallery-design-pictograms';
import type { GalleryAction } from './gallery-page';
import { FieldLabel, notifyGalleryError, useGalleryText } from './gallery-ui';
import { GalleryWatermarkDialog } from './gallery-watermark-dialog';
import { PlanLockBadge } from './plan-lock-badge';

interface GalleryDesignProps {
  gallery: Gallery;
  token?: string;
  run: GalleryAction;
}

type LookField = 'style' | 'layout' | 'spacing' | 'photoShape';

// The Design view (spec decisions 5, 6, 10): look controls on the left, the real guest page on the
// right. Every change saves at once through `updateGallery` / `setGalleryCover`; the preview shows
// the saved gallery and reloads after each successful save.
export function GalleryDesign({ gallery, token, run }: GalleryDesignProps) {
  const { t } = useGalleryText();
  const d = t.design;
  const s = t.settings;
  const [pending, setPending] = useState<UpdateGalleryInput>({});
  const [coverBusy, setCoverBusy] = useState<'upload' | 'pick' | null>(null);
  const [isWatermarkOpen, setIsWatermarkOpen] = useState(false);
  const [previewVersion, setPreviewVersion] = useState(0);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const features = gallery.planFeatures;
  const reloadPreview = () => setPreviewVersion((version) => version + 1);

  const shown = {
    style: pending.style ?? gallery.style,
    layout: pending.layout ?? gallery.layout,
    spacing: pending.spacing ?? gallery.spacing ?? FREE_GALLERY_SPACING,
    photoShape: pending.photoShape ?? gallery.photoShape ?? FREE_GALLERY_PHOTO_SHAPE,
    watermark: pending.watermark ?? gallery.watermark === true,
  };
  const isSaving = Object.keys(pending).length > 0;

  const save = async (input: UpdateGalleryInput) => {
    setPending((current) => ({ ...current, ...input }));
    const ok = await run(() => updateGalleryRequest(gallery.uuid, input, token));
    setPending((current) => {
      const next = { ...current };
      for (const key of Object.keys(input) as Array<keyof UpdateGalleryInput>) {
        delete next[key];
      }
      return next;
    });
    if (ok) {
      reloadPreview();
    }
  };

  const choose = <K extends LookField>(field: K, value: Gallery[K] | null) => {
    if (value && value !== shown[field]) {
      void save({ [field]: value } as UpdateGalleryInput);
    }
  };

  const pickCover = async (itemUuid: string) => {
    setCoverBusy('pick');
    const ok = await run(() => setGalleryCoverRequest(gallery.uuid, itemUuid, token));
    setCoverBusy(null);
    if (ok) {
      reloadPreview();
    }
  };

  const uploadCover = async (file: File | undefined) => {
    if (!file) {
      return;
    }
    const refusal = checkGalleryCoverFile(file);
    if (refusal) {
      notifyGalleryError(t.title, d.coverErrors[refusal]);
      return;
    }
    setCoverBusy('upload');
    try {
      await uploadGalleryCoverRequest(gallery.uuid, file, token);
    } catch (error) {
      const code = error instanceof DirectUploadError ? error.code : undefined;
      notifyGalleryError(
        t.title,
        code === 'STORAGE_LIMIT_EXCEEDED' ? d.coverErrors.STORAGE_LIMIT_EXCEEDED : d.coverErrors.FAILED
      );
      setCoverBusy(null);
      return;
    }
    // The page reads the gallery again; the cover stays "Preparing" until its copies are made.
    const ok = await run(async () => undefined, { success: d.coverUploadedDone });
    setCoverBusy(null);
    if (ok) {
      reloadPreview();
    }
  };

  const lookLocked = features?.look === false;
  const watermarkLocked = isPaidSettingLocked(features, 'watermark', !shown.watermark);
  const progress = gallery.watermark ? gallery.watermarkProgress : null;
  const cover = gallery.cover;
  const coverUrl = cover?.thumb800Url ?? cover?.webUrl ?? null;
  const candidates = coverCandidates(gallery);

  return (
    <Grid container spacing={3} data-testid="gallery-design">
      <Grid item xs={12} lg={5}>
        <Card sx={{ p: 3 }}>
          <SoftBox display="flex" flexDirection="column" gap={3}>
            <SoftBox display="flex" alignItems="center" gap={1}>
              <SoftTypography variant="h5">{d.title}</SoftTypography>
              <SoftBox flex={1} />
              {isSaving ? (
                <SoftTypography variant="caption" color="text" data-testid="gallery-design-saving">
                  {d.saving}
                </SoftTypography>
              ) : null}
            </SoftBox>
            <SoftTypography variant="caption" color="text" mt={-2}>
              {d.intro}
            </SoftTypography>

            <Block label={d.coverLabel} testId="gallery-design-cover">
              <SoftBox display="flex" gap={2} alignItems="center">
                <SoftBox
                  width={120}
                  height={80}
                  borderRadius="md"
                  overflow="hidden"
                  bgcolor="grey.300"
                  flexShrink={0}
                  data-testid="gallery-design-cover-current"
                >
                  {coverUrl ? (
                    <img
                      src={coverUrl}
                      alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  ) : null}
                </SoftBox>
                <SoftBox display="flex" flexDirection="column" gap={1} alignItems="flex-start">
                  <SoftTypography variant="button" fontWeight="regular" data-testid="gallery-design-cover-state">
                    {!cover
                      ? d.coverNone
                      : cover.state === 'PROCESSING'
                      ? d.coverPreparing
                      : cover.uploaded
                      ? d.coverUploaded
                      : d.coverPicked}
                  </SoftTypography>
                  <SoftButton
                    variant="outlined"
                    color="info"
                    size="small"
                    disabled={coverBusy !== null}
                    onClick={() => fileInput.current?.click()}
                    data-testid="gallery-design-cover-upload"
                  >
                    {coverBusy === 'upload' ? d.coverUploading : d.coverUpload}
                  </SoftButton>
                  <input
                    ref={fileInput}
                    type="file"
                    accept="image/jpeg,image/png"
                    hidden
                    data-testid="gallery-design-cover-input"
                    onChange={(event) => {
                      void uploadCover(event.target.files?.[0]);
                      event.target.value = '';
                    }}
                  />
                </SoftBox>
              </SoftBox>
              <SoftTypography variant="caption" color="text">
                {candidates.length ? d.coverPickLabel : d.coverEmpty}
              </SoftTypography>
              {candidates.length ? (
                <SoftBox
                  display="flex"
                  gap={1}
                  sx={{ overflowX: 'auto', pb: 0.5 }}
                  data-testid="gallery-design-cover-items"
                >
                  {candidates.map((item) => {
                    const isCover = !cover?.uploaded && cover?.itemUuid === item.uuid;
                    return (
                      <SoftBox
                        key={item.uuid}
                        component="button"
                        type="button"
                        disabled={coverBusy !== null || isCover}
                        onClick={() => void pickCover(item.uuid)}
                        aria-label={interpolateTemplate(d.coverPickItem, { name: item.media.fileName })}
                        aria-pressed={isCover}
                        data-testid={`gallery-design-cover-item-${item.uuid}`}
                        sx={{
                          p: 0,
                          width: 64,
                          height: 64,
                          flexShrink: 0,
                          borderRadius: 1,
                          overflow: 'hidden',
                          cursor: isCover ? 'default' : 'pointer',
                          border: 'none',
                          outline: isCover ? '3px solid' : 'none',
                          outlineColor: 'info.main',
                          outlineOffset: -3,
                          bgcolor: 'grey.300',
                        }}
                      >
                        <img
                          src={item.thumb400Url ?? undefined}
                          alt=""
                          loading="lazy"
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      </SoftBox>
                    );
                  })}
                </SoftBox>
              ) : null}
              <SoftTypography variant="caption" color="text">
                {d.coverHint}
              </SoftTypography>
            </Block>

            <Block label={d.styleLabel} testId="gallery-design-style">
              <Choices
                value={shown.style}
                options={GALLERY_STYLES}
                group="style"
                labels={s.styles}
                disabled={() => isSaving}
                onChange={(value) => choose('style', value)}
                pictogram={(style) => <StylePictogram style={style} />}
              />
            </Block>

            <Block label={d.layoutLabel} testId="gallery-design-layout">
              <Choices
                value={shown.layout}
                options={GALLERY_LAYOUTS}
                group="layout"
                labels={s.layouts}
                disabled={() => isSaving}
                onChange={(value) => choose('layout', value)}
                pictogram={(layout) => <LayoutPictogram layout={layout} />}
              />
              <SoftTypography variant="caption" color="text">
                {d.lookHint}
              </SoftTypography>
            </Block>

            <Block
              label={d.shapeLabel}
              testId="gallery-design-shape"
              lock={lookLocked ? 'gallery-design-shape-lock' : undefined}
            >
              <Choices
                value={shown.photoShape}
                options={GALLERY_PHOTO_SHAPES}
                group="shape"
                labels={d.shapes}
                disabled={(shape) =>
                  isSaving || isPaidOptionLocked(features, 'look', shape, FREE_GALLERY_PHOTO_SHAPE)
                }
                onChange={(value) => choose('photoShape', value)}
                pictogram={(shape) => <ShapePictogram shape={shape} />}
              />
            </Block>

            <Block
              label={d.spacingLabel}
              testId="gallery-design-spacing"
              lock={lookLocked ? 'gallery-design-spacing-lock' : undefined}
            >
              <Choices
                value={shown.spacing}
                options={GALLERY_SPACINGS}
                group="spacing"
                labels={d.spacings}
                disabled={(spacing) =>
                  isSaving || isPaidOptionLocked(features, 'look', spacing, FREE_GALLERY_SPACING)
                }
                onChange={(value) => choose('spacing', value)}
                pictogram={(spacing) => <SpacingPictogram spacing={spacing} />}
              />
              {lookLocked ? (
                <SoftTypography variant="caption" color="text" data-testid="gallery-design-look-locked">
                  {t.plan.lockedHint}
                </SoftTypography>
              ) : null}
            </Block>

            <Block
              label={s.watermarkLabel}
              testId="gallery-design-watermark"
              lock={watermarkLocked ? 'gallery-design-watermark-lock' : undefined}
            >
              <SoftBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Switch
                  checked={shown.watermark}
                  disabled={watermarkLocked || isSaving}
                  onChange={(event) => void save({ watermark: event.target.checked })}
                  inputProps={{ 'aria-label': s.watermarkSwitch }}
                  data-testid="gallery-design-watermark-switch"
                />
                <SoftTypography variant="button" fontWeight="regular">
                  {s.watermarkSwitch}
                </SoftTypography>
              </SoftBox>
              {progress ? (
                <SoftBox data-testid="gallery-design-watermark-progress">
                  <SoftTypography variant="button" fontWeight="medium" display="block">
                    {interpolateTemplate(d.watermarkProgress, {
                      ready: String(progress.ready),
                      total: String(progress.total),
                    })}
                  </SoftTypography>
                  {progress.failed > 0 ? (
                    <SoftTypography
                      variant="caption"
                      color="error"
                      display="block"
                      data-testid="gallery-design-watermark-failed"
                    >
                      {interpolateTemplate(d.watermarkFailed, { count: String(progress.failed) })}
                    </SoftTypography>
                  ) : null}
                </SoftBox>
              ) : null}
              <SoftTypography variant="caption" color="text" data-testid="gallery-design-watermark-hint">
                {watermarkLocked
                  ? t.plan.lockedHint
                  : showsOriginalsWithoutWatermarkHint({ ...gallery, watermark: shown.watermark })
                  ? s.watermarkOriginalsHint
                  : s.watermarkHint}
              </SoftTypography>
              <SoftBox>
                <SoftButton
                  variant="text"
                  color="info"
                  size="small"
                  onClick={() => setIsWatermarkOpen(true)}
                  data-testid="gallery-design-watermark-edit"
                  sx={{ px: 0 }}
                >
                  {s.watermarkEdit}
                </SoftButton>
              </SoftBox>
            </Block>
          </SoftBox>
        </Card>
      </Grid>
      <Grid item xs={12} lg={7}>
        <GalleryPreview gallery={gallery} version={previewVersion} token={token} />
      </Grid>
      {isWatermarkOpen ? (
        <GalleryWatermarkDialog
          token={token}
          onClose={() => {
            setIsWatermarkOpen(false);
            // A saved template re-renders the copies: read the progress again and show it.
            void run(async () => undefined).then((ok) => ok && reloadPreview());
          }}
        />
      ) : null}
    </Grid>
  );
}

function Block({
  label,
  testId,
  lock,
  children,
}: {
  label: string;
  testId: string;
  lock?: string;
  children: ReactNode;
}) {
  return (
    <SoftBox display="flex" flexDirection="column" gap={1} data-testid={testId}>
      <SoftBox display="flex" alignItems="center" gap={1}>
        <FieldLabel>{label}</FieldLabel>
        {lock ? <PlanLockBadge testId={lock} /> : null}
      </SoftBox>
      {children}
    </SoftBox>
  );
}

interface ChoicesProps<T extends string> {
  value: T;
  options: T[];
  group: string;
  labels: Record<T, string>;
  disabled: (option: T) => boolean;
  onChange: (value: T | null) => void;
  pictogram: (option: T) => ReactNode;
}

// Pictogram tiles of one look choice; the chosen one stays enabled so it always reads as chosen.
function Choices<T extends string>({
  value,
  options,
  group,
  labels,
  disabled,
  onChange,
  pictogram,
}: ChoicesProps<T>) {
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value}
      onChange={(_event, next: T | null) => onChange(next)}
      sx={{ flexWrap: 'wrap', gap: 1, '& .MuiToggleButtonGroup-grouped': { border: 1, borderRadius: '8px !important', m: 0 } }}
    >
      {options.map((option) => (
        <ToggleButton
          key={option}
          value={option}
          disabled={option !== value && disabled(option)}
          aria-label={labels[option]}
          data-testid={`gallery-design-${group}-${option}`}
          sx={{ flexDirection: 'column', gap: 0.5, px: 1.5, py: 1, textTransform: 'none', minWidth: 84 }}
        >
          {pictogram(option)}
          <SoftBox component="span" fontSize={12} lineHeight={1.2}>
            {labels[option]}
          </SoftBox>
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

// The real guest page (`Gallery.link`) at a desktop or phone width, scaled down to the column,
// opened with the owner's preview token (`#preview=<token>`): no password screen, nothing counted.
// Remounted (so reloaded) after every save, when an uploaded cover becomes ready and with each new
// token, asked for shortly before the last one runs out. Without a token (the request failed) it
// shows the page as a guest sees it.
function GalleryPreview({ gallery, version, token }: { gallery: Gallery; version: number; token?: string }) {
  const { t } = useGalleryText();
  const d = t.design;
  const [frame, setFrame] = useState<PreviewFrame>('desktop');
  const [width, setWidth] = useState(0);
  const [preview, setPreview] = useState<{ token: string | null } | null>(null);
  const box = useRef<HTMLDivElement | null>(null);
  const link = gallery.link?.trim() || null;

  useEffect(() => {
    if (!link) {
      return undefined;
    }
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const ask = async () => {
      try {
        const next = await createGalleryPreviewTokenRequest(gallery.uuid, token);
        if (!active) {
          return;
        }
        setPreview({ token: next.token });
        timer = setTimeout(() => void ask(), previewTokenRefreshDelay(next.expiresAt));
      } catch {
        if (active) {
          setPreview((current) => current ?? { token: null });
        }
      }
    };
    setPreview(null);
    void ask();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [gallery.uuid, gallery.link, link, token]);

  useEffect(() => {
    const element = box.current;
    if (!element) {
      return undefined;
    }
    setWidth(element.clientWidth);
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(() => setWidth(element.clientWidth));
    observer.observe(element);
    return () => observer.disconnect();
  }, [link]);

  const size = PREVIEW_FRAMES[frame];
  // A phone is drawn no wider than a real one would sit beside the controls.
  const scale = previewScale(frame, frame === 'phone' ? Math.min(width, 330) : width);
  const coverKey = `${gallery.cover?.uuid ?? ''}:${gallery.cover?.state ?? ''}`;

  return (
    <Card sx={{ p: 3, position: { lg: 'sticky' }, top: { lg: 16 } }} data-testid="gallery-design-preview">
      <SoftBox display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1}>
        <SoftTypography variant="h6">{d.previewLabel}</SoftTypography>
        <SoftBox flex={1} />
        <ToggleButtonGroup
          exclusive
          size="small"
          value={frame}
          onChange={(_event, next: PreviewFrame | null) => next && setFrame(next)}
        >
          <ToggleButton value="desktop" aria-label={d.desktop} data-testid="gallery-design-preview-desktop">
            <ComputerOutlinedIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="phone" aria-label={d.phone} data-testid="gallery-design-preview-phone">
            <PhoneIphoneOutlinedIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
        <SoftButton
          component="a"
          href={link ?? undefined}
          target="_blank"
          rel="noopener noreferrer"
          variant="outlined"
          color="info"
          size="small"
          disabled={!link}
          startIcon={<OpenInNewIcon />}
          data-testid="gallery-design-open-as-guest"
        >
          {t.share.openAsGuest}
        </SoftButton>
      </SoftBox>
      <SoftTypography variant="caption" color="text" display="block" mb={1}>
        {d.previewHint}
      </SoftTypography>
      <SoftBox ref={box} width="100%">
        {link && preview ? (
          <SoftBox
            mx="auto"
            width={size.width * scale}
            height={size.height * scale}
            overflow="hidden"
            border="1px solid"
            borderColor="grey.300"
            borderRadius={frame === 'phone' ? '24px' : '8px'}
            bgcolor="white"
            data-frame={frame}
            data-testid="gallery-design-preview-box"
          >
            <iframe
              key={`${frame}:${version}:${coverKey}:${preview.token ?? ''}`}
              src={galleryPreviewSrc(link, preview.token)}
              title={d.previewFrameTitle}
              width={size.width}
              height={size.height}
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
              referrerPolicy="strict-origin-when-cross-origin"
              data-testid="gallery-design-preview-frame"
              data-version={version}
              style={{
                border: 0,
                display: 'block',
                transform: `scale(${scale})`,
                transformOrigin: '0 0',
              }}
            />
          </SoftBox>
        ) : link ? (
          <SoftBox
            mx="auto"
            width={size.width * scale}
            height={size.height * scale}
            border="1px solid"
            borderColor="grey.300"
            borderRadius={frame === 'phone' ? '24px' : '8px'}
            data-testid="gallery-design-preview-loading"
          />
        ) : (
          <SoftTypography variant="button" fontWeight="regular" color="text" data-testid="gallery-design-preview-no-link">
            {t.share.noLink}
          </SoftTypography>
        )}
      </SoftBox>
    </Card>
  );
}

